import React, { useEffect, useRef, useState } from "react";
import { Lock, Delete, Fingerprint, Mail, Loader2 } from "lucide-react";
import { PIN_LENGTHS, verifyPin, lockoutRemaining, lockSettings, pinLength, biometricUnlock, resetLockAndDeviceData, pinProblem, setPin } from "./appLock.js";
import { getSession, sendLockResetCode, verifyLockResetCode } from "./sync.js";

// A numeric keypad with dots; used by the lock screen and by the PIN steps in Settings.
export function PinPad({ value, length = 6, onDigit, onBack, disabled, extra }) {
  return (
    <div className="w-full max-w-[270px] mx-auto">
      <div className="flex justify-center gap-3 mb-7" aria-label={`${value.length} of ${length} digits entered`}>
        {Array.from({ length }).map((_, i) => (
          <span key={i} className="w-3.5 h-3.5 rounded-full border" style={{ borderColor: "var(--brass)", background: i < value.length ? "var(--brass)" : "transparent" }} />
        ))}
      </div>
      <div className="grid grid-cols-3 gap-3">
        {["1", "2", "3", "4", "5", "6", "7", "8", "9"].map((d) => (
          <button key={d} onClick={() => onDigit(d)} disabled={disabled} className="h-14 rounded-full bg-card text-xl font-medium disabled:opacity-40 active:scale-95 transition-transform">{d}</button>
        ))}
        <div className="h-14 flex items-center justify-center">{extra}</div>
        <button onClick={() => onDigit("0")} disabled={disabled} className="h-14 rounded-full bg-card text-xl font-medium disabled:opacity-40 active:scale-95 transition-transform">0</button>
        <button onClick={onBack} disabled={disabled || !value} aria-label="Delete last digit" className="h-14 rounded-full flex items-center justify-center disabled:opacity-30"><Delete size={20} className="text-ivory-dim" /></button>
      </div>
    </div>
  );
}

// One step of entering a PIN (new, confirm, current...). onDone(pin) returns "" to continue or an error message to show.
export function PinStep({ title, hint, length = 6, onDone, onCancel }) {
  const [pin, setP] = useState("");
  const [err, setErr] = useState("");
  async function digit(d) {
    const next = pin + d;
    setP(next); setErr("");
    if (next.length === length) {
      const e = await onDone(next);
      if (e) { setErr(e); setP(""); }
    }
  }
  return (
    <div className="mt-4">
      <div className="text-sm font-semibold text-center">{title}</div>
      {hint && <div className="text-xs text-ivory-dim text-center mt-1 mb-4">{hint}</div>}
      <PinPad value={pin} length={length} onDigit={digit} onBack={() => setP((p) => p.slice(0, -1))} />
      <div className="h-5 mt-3 text-xs text-center" style={{ color: "var(--rust)" }}>{err}</div>
      {onCancel && <button onClick={onCancel} className="w-full mt-1 py-2 text-xs text-ivory-dim">Cancel</button>}
    </div>
  );
}

export function LengthPicker({ value, onChange }) {
  return (
    <div className="flex gap-2 mt-3" role="radiogroup" aria-label="PIN length">
      {PIN_LENGTHS.map((n) => (
        <button key={n} role="radio" aria-checked={value === n} onClick={() => onChange(n)} className="flex-1 rounded-full py-2 text-xs font-medium"
          style={{ background: value === n ? "var(--brass)" : "var(--card2)", color: value === n ? "var(--ink)" : "var(--ivory-dim)" }}>
          {n} digits{n === 6 ? " (stronger)" : ""}
        </button>
      ))}
    </div>
  );
}

const maskEmail = (e) => { const [u, d] = String(e).split("@"); return `${u.slice(0, 1)}${"•".repeat(Math.max(2, Math.min(5, u.length - 1)))}@${d}`; };

// Forgot PIN, account route: a one-time code sent to the account's email proves it's the owner, then a new PIN is chosen.
// Nothing is erased on this route.
function EmailReset({ email, onDone, onBack }) {
  const [phase, setPhase] = useState("start");     // start | code | newpin | confirm
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [len, setLen] = useState(6);
  const [first, setFirst] = useState("");

  async function send() {
    setBusy(true); setErr("");
    const { error } = await sendLockResetCode(email);
    setBusy(false);
    if (error) setErr(/rate|seconds|too many/i.test(error.message) ? "Please wait a minute before asking for another code." : error.message || "Couldn't send the code.");
    else setPhase("code");
  }
  async function verify() {
    if (!code.trim()) return;
    setBusy(true); setErr("");
    const { error } = await verifyLockResetCode(email, code.trim());
    setBusy(false);
    if (error) setErr("That code didn't work. Check it, or ask for a new one.");
    else setPhase("newpin");
  }

  if (phase === "newpin" || phase === "confirm") {
    return (
      <div>
        <div className="text-xs text-ivory-dim text-center mb-1">Email confirmed. Choose a new PIN.</div>
        {phase === "newpin" && <LengthPicker value={len} onChange={setLen} />}
        {phase === "newpin"
          ? <PinStep key={`n${len}`} title="New PIN" length={len} onDone={(p) => { const e = pinProblem(p, len); if (e) return e; setFirst(p); setPhase("confirm"); return ""; }} />
          : <PinStep key="c" title="Type it again" length={len} onDone={async (p) => { if (p !== first) { setPhase("newpin"); return "Those didn't match. Start again."; } await setPin(p); onDone(); return ""; }} />}
      </div>
    );
  }

  return (
    <div className="text-left">
      {phase === "start" ? (
        <>
          <p className="text-xs text-ivory-dim leading-relaxed">We'll email a one-time code to <span className="text-ivory">{maskEmail(email)}</span>. Enter it here to choose a new PIN. Nothing on this device is erased.</p>
          <button onClick={send} disabled={busy} className="mt-3 w-full flex items-center justify-center gap-2 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : <Mail size={15} />} Email me a code</button>
        </>
      ) : (
        <>
          <p className="text-xs text-ivory-dim leading-relaxed">Enter the code we sent to {maskEmail(email)}. It can take a minute to arrive; check your junk folder too.</p>
          <input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => e.key === "Enter" && verify()} inputMode="numeric" autoComplete="one-time-code" maxLength={10} autoFocus placeholder="Code" className="w-full mt-3 rounded-lg px-3 py-2.5 text-sm text-center tracking-widest v-mono" style={{ background: "var(--card2)", color: "var(--ivory)", border: "1px solid var(--hair)" }} />
          <button onClick={verify} disabled={busy || !code.trim()} className="mt-3 w-full flex items-center justify-center gap-2 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm disabled:opacity-50">{busy ? <Loader2 size={15} className="animate-spin" /> : null} Confirm code</button>
          <button onClick={send} disabled={busy} className="mt-2 w-full text-xs text-ivory-dim py-1.5">Send a new code</button>
        </>
      )}
      {err && <div className="text-xs mt-2" style={{ color: "var(--rust)" }}>{err}</div>}
      <button onClick={onBack} className="mt-1 w-full text-xs text-ivory-dim py-1.5">Back</button>
    </div>
  );
}

export default function LockScreen({ onUnlock }) {
  const len = pinLength();
  const [pin, setPinValue] = useState("");
  const [msg, setMsg] = useState("");
  const [wait, setWait] = useState(lockoutRemaining());
  const [forgot, setForgot] = useState(false);
  const [emailRoute, setEmailRoute] = useState(false);
  const [email, setEmail] = useState("");
  const busy = useRef(false);
  const bio = lockSettings()?.biometric;

  useEffect(() => { if (wait <= 0) return; const t = setInterval(() => setWait(lockoutRemaining()), 500); return () => clearInterval(t); }, [wait]);
  useEffect(() => { if (bio) tryBio(); /* offer it straight away; the PIN is always there too */ }, []); // eslint-disable-line
  useEffect(() => { getSession().then((s) => setEmail(s?.user?.email || "")).catch(() => {}); }, []);

  async function tryBio() { if (await biometricUnlock()) onUnlock(); }

  async function digit(d) {
    if (busy.current || wait > 0) return;
    const next = pin + d;
    setPinValue(next); setMsg("");
    if (next.length < len) return;
    busy.current = true;
    const r = await verifyPin(next);
    busy.current = false;
    if (r.ok) { onUnlock(); return; }
    setPinValue("");
    if (r.wait) { setWait(r.wait); setMsg("Too many wrong tries."); }
    else setMsg(`Wrong PIN. ${r.triesLeft} ${r.triesLeft === 1 ? "try" : "tries"} left before a pause.`);
  }

  function erase() {
    if (!window.confirm("Erase everything on this device and start over? If you sync to an account (or have a backup file) you can get your data back. Otherwise it's gone.")) return;
    resetLockAndDeviceData();
    window.location.reload();
  }

  return (
    <div className="vellum-app min-h-screen w-full flex items-center justify-center p-6" style={{ background: "var(--ink)", color: "var(--ivory)" }}>
      <style>{`.vellum-app{--ink:#0F0B14;--card:#1B1420;--card2:#251C2C;--ivory:#F4E9D8;--ivory-dim:rgba(244,233,216,0.58);--brass:#D9B26B;--rust:#B5445F;} .vellum-app{font-family:'Inter Variable','Inter',system-ui,sans-serif;} .v-display{font-family:'Fraunces Variable','Fraunces',Georgia,serif;} .bg-card{background:var(--card)} .bg-card2{background:var(--card2)} .text-ivory-dim{color:var(--ivory-dim)} .text-ivory{color:var(--ivory)} .text-brass{color:var(--brass)} .v-mono{font-family:'Inter Variable','Inter',system-ui,sans-serif;font-variant-numeric:tabular-nums} .bg-gold-grad{background:linear-gradient(135deg,#E8C98A,#C9A15B)} .text-ink{color:#1a1320}`}</style>
      <div className="w-full max-w-sm text-center">
        <Lock size={26} className="text-brass mx-auto mb-3" />
        <h1 className="v-display text-2xl font-semibold mb-1">Balance &amp; Beyond</h1>

        {emailRoute ? (
          <div className="mt-6 bg-card rounded-2xl p-4">
            <EmailReset email={email} onDone={onUnlock} onBack={() => setEmailRoute(false)} />
          </div>
        ) : (
          <>
            <p className="text-xs text-ivory-dim mb-7">{wait > 0 ? `Try again in ${wait >= 60 ? `${Math.ceil(wait / 60)} min` : `${wait} s`}.` : "Enter your PIN"}</p>
            <PinPad value={pin} length={len} onDigit={digit} onBack={() => setPinValue((p) => p.slice(0, -1))} disabled={wait > 0}
              extra={bio ? <button onClick={tryBio} aria-label="Unlock with fingerprint or face" className="h-14 w-14 rounded-full flex items-center justify-center"><Fingerprint size={24} className="text-brass" /></button> : null} />
            <div className="h-5 mt-5 text-xs" style={{ color: "var(--rust)" }}>{msg}</div>
            {!forgot ? (
              <button onClick={() => setForgot(true)} className="mt-4 text-xs text-ivory-dim underline">Forgot PIN?</button>
            ) : (
              <div className="mt-4 bg-card rounded-2xl p-4 text-left">
                {email ? (
                  <>
                    <p className="text-xs text-ivory-dim leading-relaxed">You're signed in to an account on this device, so you can reset your PIN with a code sent to its email. Your data stays put.</p>
                    <button onClick={() => setEmailRoute(true)} className="mt-3 w-full flex items-center justify-center gap-2 bg-gold-grad text-ink font-semibold rounded-full py-2.5 text-sm"><Mail size={15} /> Reset with my account email</button>
                    <div className="text-[11px] text-ivory-dim text-center my-3">or</div>
                  </>
                ) : (
                  <p className="text-xs text-ivory-dim leading-relaxed mb-3">There's no account on this device, so a forgotten PIN can't be reset by email. (Signing in to an account in Settings, before you forget, makes that possible.)</p>
                )}
                <p className="text-xs text-ivory-dim leading-relaxed">You can also erase this device's data and start over. If you sync to an account, signing in again brings your data back (you'll need your encryption passphrase if you turned that on). A backup file works too.</p>
                <button onClick={erase} className="mt-3 w-full rounded-full py-2 text-sm font-medium bg-card2" style={{ color: "var(--rust)" }}>Erase data on this device</button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
